/** A batch action the server refused for the whole selection (nothing was queued), said
 *  where the operator asked instead of in a snackbar: the server's sentence, then each
 *  refused item — its id, a link to it when it has a page, and why. A long list stops at
 *  LIST_MAX and says how many more. Rendered by ResourceDataGrid from useBatchAction's
 *  `refusal`; only actions that opt into `refusals` ever produce one. */
import { Box, Link, Typography } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { AppDialog } from '@/components/AppDialog';

import type { BatchRefusal } from './batchTypes';

/** Items listed before "and N more". */
export const LIST_MAX = 20;

interface Props {
  /** The refusal to explain; the dialog is open while there is one. */
  refusal: BatchRefusal | null;
  onClose: () => void;
}

export function BatchRefusalDialog({ refusal: current, onClose }: Props) {
  // The last refusal stays drawn while the dialog fades out, so it never empties first.
  const [last, setLast] = useState(current);
  if (current && current !== last) setLast(current);
  const refusal = current ?? last;
  const items = refusal?.items ?? [];
  const shown = items.slice(0, LIST_MAX);
  const more = items.length - shown.length;
  return (
    <AppDialog open={current !== null} onClose={onClose} title={refusal?.title ?? ''}>
      <Typography variant="body2">{refusal?.detail}</Typography>
      {shown.length > 0 && (
        <Box component="ul" sx={{ my: 1, pl: 3 }}>
          {shown.map((item) => (
            <Typography
              key={String(item.id)}
              component="li"
              variant="body2"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {item.to ? (
                <Link component={RouterLink} to={item.to}>
                  #{item.id}
                </Link>
              ) : (
                `#${item.id}`
              )}
              {` · ${item.reason}`}
            </Typography>
          ))}
        </Box>
      )}
      {more > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          and {more.toLocaleString()} more
        </Typography>
      )}
    </AppDialog>
  );
}
